import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Pagination from '../Pagination/Pagination';
import { updateFilter, resetFilter, clearFilter, updatePagination } from '../../actions/environment';
import { addClass, removeClass, addEvent, removeEvent, offset } from '../../utils/dom';
import { intersect, unique } from '../../utils/array';
import { translate, prefix } from '../../utils/locale';
import { deepCompare } from '../../utils/object';
import s from './Filter.scss';

let scrolledFilterPosition = null;

class Filter extends Component {
	constructor(props) {
		super(props);

		this.state = {
			categories: false,
			filterCategories: false,
			colors: false,
			finishes: false,
			sort: false,
			smallScreenFilter: false,
			layout: 'default',
			screenHeight: '0px',
			hash: typeof window !== 'undefined' ? window.location.hash : '',
		};

		this.filterTools = React.createRef();
	}

	componentDidMount() {
		if (this.state.hash) {
			const hashFilter = [];
			this.state.hash.slice(1).split('&').forEach((filterPart) => {
				const filterParts = filterPart.split('=');
				if (filterParts.length === 1) {
					hashFilter.push({ [filterParts[0]]: true });
				} else {
					hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
				}
			});

			this.props.updateFilter(hashFilter, true, this.props.setup, this.props.currentPath);
			this.setState({ hash: null });
		}

		// Wait for page to be rendered with all relevant elements.
		const initiateSiteListeners = () => {
			if (!this.filterTools?.current) {
				setTimeout(initiateSiteListeners, 200);
			}
			const $html = document.querySelector('html');
			let scrolledFilterFixed = false;

			const scrollEvent = () => {
				const top = window.pageYOffset || document.documentElement.scrollTop;
				if (top < 10) this.getOffset();

				// make logo sticky when level with header.
				if (!scrolledFilterFixed && top >= scrolledFilterPosition) {
					scrolledFilterFixed = true;
					addClass($html, 'u_pageIsScrolledFilter');
				} else if (scrolledFilterFixed && (top + 55) < scrolledFilterPosition) {
					scrolledFilterFixed = false;
					removeClass($html, 'u_pageIsScrolledFilter');
				}
			};

			addEvent(window, 'scroll.filterTools', () => {
				window.requestAnimationFrame(scrollEvent);
			});

			scrollEvent();
		};

		initiateSiteListeners();
	}

	componentDidUpdate({ filter }) {
		const { filteredBy } = this.props.filter;
		if (!deepCompare(filter.filteredBy, filteredBy)) this.getOffset();
	}

	componentWillUnmount() {
		removeClass(document.body, 'u_trippleListing');
		removeClass(document.body, 'u_singleListing');
		removeClass(document.querySelector('html'), 'u_pageIsScrollingUp');
		removeEvent(window, 'scroll.filterTools');
	}

	getOffset = () => {
		if (this.filterTools?.current) scrolledFilterPosition = offset(this.filterTools.current).top;
	}

	handleClearFilter = (e) => {
		e.preventDefault();
		this.props.clearFilter();
		this.props.updatePagination(this.props.currentPath, 1);
		this.resetFilterDropdowns();
	}

	resetFilterDropdowns = (e) => {
		if (e) e.preventDefault();
		this.setState({
			filterCategories: false,
			colors: false,
			finishes: false,
			sort: false,
			smallScreenFilter: false,
			screenHeight: '0px',
		});
		if (window.innerWidth <= 600) {
			window.scrollTo(0, 0);
		}
	}

	handleToggleGroup = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const whichGroup = $link.dataset.group;
		const nextState = {
			filterCategories: false,
			colors: false,
			finishes: false,
			sort: false,
			screenHeight: `${window.innerHeight - 95}px`,
		};

		this.setState((prevState) => {
			if (!prevState[whichGroup]) nextState[whichGroup] = true;
			return nextState;
		});
	}

	handleToggleLayout = (e) => {
		e.preventDefault();
		this.setState((prevState) => {
			if (prevState.layout === 'default') {
				addClass(document.body, 'u_trippleListing');
				return { layout: 'tripple' };
			}
			if (prevState.layout === 'tripple') {
				removeClass(document.body, 'u_trippleListing');
				addClass(document.body, 'u_singleListing');
				return { layout: 'single' };
			}
			removeClass(document.body, 'u_singleListing');
			return { layout: 'default' };
		});
	}

	handleFilterUpdate = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const action = $link.getAttribute('href');
		const filterType = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const filterValue = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(prefix(this.props.setup, ''), '');

		this.props.updateFilter([{ [filterType]: filterValue }], false, this.props.setup, this.props.currentPath);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	render() {
		const { ListComponent, location, data, filtering, setup, filter, currentPath, contentSections, contentOffset } = this.props;
		const { screenHeight, layout } = this.state;

		// how to update filterHash on page load???

		const { filterArrays, filterDescriptions, localized, products } = filtering;
		const isLocalized = localized[setup.language];
		const isFiltered = !deepCompare(filter.clearFilterBase, filter.filteredBy);

		if (!filter.currentFilterSegment) return null;

		// Category paths are localized.
		const categories = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
			return ({ ...acc, [categoryKey]: [...filterArrays.categories[category]] });
		}, {}) : filterArrays.categoryVariants;

		// Localize categories
		const filterCategories = isLocalized ? Object.keys(filterArrays.filterCategories).reduce((acc, category) => {
			const categorykey = isLocalized.filterCategories[category] ? isLocalized.filterCategories[category].key : category;
			return ({ ...acc, [categorykey]: [...filterArrays.filterCategories[category]] });
		}, {}) : filterArrays.filterCategories;

		const filterCategoryDescriptions = isLocalized ? Object.keys(filterArrays.filterCategories).reduce((acc, category) => {
			const categoryKey = isLocalized.filterCategories[category] ? isLocalized.filterCategories[category].key : category;
			const categoryDescription = isLocalized.filterCategories[category] ? isLocalized.filterCategories[category].description : filterDescriptions.filterCategories[category];
			return ({ ...acc, [categoryKey]: categoryDescription });
		}, {}) : filterDescriptions.filterCategories;

		// Localize colors
		const colors = isLocalized ? Object.keys(filterArrays.colors).reduce((acc, color) => {
			const colorkey = isLocalized.colors[color] ? isLocalized.colors[color].key : color;
			return ({ ...acc, [colorkey]: [...filterArrays.colors[color]] });
		}, {}) : filterArrays.colors;

		const colorDescriptions = isLocalized ? Object.keys(filterArrays.colors).reduce((acc, color) => {
			const colorKey = isLocalized.colors[color] ? isLocalized.colors[color].key : color;
			const colorDescription = isLocalized.colors[color] ? isLocalized.colors[color].description : filterDescriptions.colors[color];
			return ({ ...acc, [colorKey]: colorDescription });
		}, {}) : filterDescriptions.colors;

		// Localize finishes
		const finishes = isLocalized ? Object.keys(filterArrays.finishes).reduce((acc, finish) => {
			const finishkey = isLocalized.finishes[finish] ? isLocalized.finishes[finish].key : finish;
			return ({ ...acc, [finishkey]: [...filterArrays.finishes[finish]] });
		}, {}) : filterArrays.finishes;

		const finishDescriptions = isLocalized ? Object.keys(filterArrays.finishes).reduce((acc, finish) => {
			const finishKey = isLocalized.finishes[finish] ? isLocalized.finishes[finish].key : finish;
			const finishDescription = isLocalized.finishes[finish] ? isLocalized.finishes[finish].description : filterDescriptions.finishes[finish];
			return ({ ...acc, [finishKey]: finishDescription });
		}, {}) : filterDescriptions.finishes;

		/*
			Filter

			This could benefit from memoization - reselect

			Filters which don't match the base list of products are removed i.e. women men etc.
			Filters which don't match the current categories' list of products are disabled. Except categories.
		*/

		// Get base URI for filtering.
		if (data.baseUri === undefined) return null;

		let relevantBaseProducts = [];
		if (data.baseType === 'campaigns') relevantBaseProducts = filterArrays[data.campaignType];
		if (data.baseType === 'categories') relevantBaseProducts = categories[data.baseUri];
		if (relevantBaseProducts === undefined) relevantBaseProducts = [];
		relevantBaseProducts = relevantBaseProducts.filter((rbp) => products[rbp] !== undefined);
		let relevantProducts = [...relevantBaseProducts];

		const filteredProducts = {};

		// Filter only relevant products
		filter.filteredBy.forEach((filterObj) => {
			const filterType = Object.keys(filterObj)[0];
			const filterItem = filterObj[filterType];
			if (filterItem === true || (Array.isArray(filterItem) && filterItem.length > 0)) {
				// Products are filtered
				// Need to update relevantProducts on every pass.
				switch (filterType) {
					case 'categories': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...(categories[type] ? categories[type] : []), ...acc], []));
						filteredProducts.categories = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
						break;
					}
					case 'filterCategories': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...(filterCategories[type] ? filterCategories[type] : []), ...acc], []));
						filteredProducts.filterCategories = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
						break;
					}
					case 'colors': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...colors[type], ...acc], []));
						filteredProducts.colors = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
						break;
					}
					case 'finishes': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...finishes[type], ...acc], []));
						filteredProducts.finishes = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
						break;
					}
					default:
						break;
				}
			}
		});

		// Last filter is special so that the user can select multiple filters.
		let relevantFilterProducts = [...relevantBaseProducts];
		let relevantFilterProductsLast = [...relevantBaseProducts];
		let lastFilter = '';

		if (Object.keys(filteredProducts).length > 0) {
			// Restrict filters based on final filter in filteredBy.
			filter.filteredBy.slice(0).forEach((filterObj, i) => {
				lastFilter = Object.keys(filterObj)[0];
				if (filteredProducts[lastFilter]) {
					relevantFilterProducts = intersect(relevantFilterProducts, filteredProducts[lastFilter]);
					if (i < filter.filteredBy.length - 1) relevantFilterProductsLast = intersect(relevantFilterProductsLast, filteredProducts[lastFilter]);
				}
			});
		}

		/*
			Sorting
		*/
		if (filter.sort) {
			const sortBy = filter.sort.replace(/-(ascending|descending)$/, '');

			relevantProducts = relevantProducts.map((productId) => {
				let sortingValue = filter.sort.indexOf('descending') !== -1 ? 0 : 9999999999;
				if (products[productId].inStock === 0) return { key: productId, sort: sortingValue };

				if (sortBy === 'price') {
					sortingValue = parseInt(products[productId].price.EUR.price, 10);
				} else if (products[productId][sortBy]) {
					sortingValue = products[productId][sortBy];
				}
				return { key: productId, sort: sortingValue };
			}).sort((a, b) => {
				if (a.sort === b.sort) return parseInt(a.key, 10) > parseInt(b.key, 10) ? 1 : -1;
				if (filter.sort.indexOf('descending') === -1) {
					return a.sort > b.sort ? 1 : -1;
				}
				return a.sort < b.sort ? 1 : -1;
			}).map((productObject) => productObject.key);
		}

		/*
			Class manipulation
		*/

		const filterOpen = this.state.filterCategories || this.state.colors || this.state.finishes || this.state.sort;
		const filterToolsClass = [s.filter_tools];
		if (relevantBaseProducts.length === 0) filterToolsClass.push(s.filter_tools__empty);
		if (this.state.smallScreenFilter) filterToolsClass.push(s.filter_tools__open);

		let smallScreenFilterClass = s.filtering_inner_smallScreen;
		if (this.state.smallScreenFilter) smallScreenFilterClass = s.filtering_inner_smallScreen__open;

		return (
			<div className={[s.filter, (filterOpen ? s.filter__open : null)].join(' ')}>
				<div className={filterToolsClass.join(' ')} ref={this.filterTools}>
					<div className={s.filter_tools_fixed}>
						<div className={s.filtering__open}>
							<div className={[s.filtering_refineWrapper, this.state.smallScreenFilter ? s.filtering_refineWrapper__expanded : null].join(' ')}>
								<div className={[s.filtering_refineWrapper_item, s.filtering_refineWrapper_item__refine].join(' ')}>
									<a href="#open-group" data-group="smallScreenFilter" onClick={this.handleToggleGroup}>
										<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M0 4.9375H10" /><path d="M0 8.6875H10" /><path d="M0 1.25H10" /><path d="M4.225 4.9375C4.225 5.54502 3.73251 6.0375 3.125 6.0375C2.51749 6.0375 2.025 5.54502 2.025 4.9375C2.025 4.32999 2.51749 3.8375 3.125 3.8375C3.73251 3.8375 4.225 4.32999 4.225 4.9375Z" fill="white" /><path d="M8.6 8.6875C8.6 9.29502 8.10751 9.7875 7.5 9.7875C6.89249 9.7875 6.4 9.29502 6.4 8.6875C6.4 8.07999 6.89249 7.5875 7.5 7.5875C8.10751 7.5875 8.6 8.07999 8.6 8.6875Z" fill="white" /> <path d="M7.0375 1.25C7.0375 1.85751 6.54501 2.35 5.9375 2.35C5.32999 2.35 4.8375 1.85751 4.8375 1.25C4.8375 0.642487 5.32999 0.15 5.9375 0.15C6.54501 0.15 7.0375 0.642487 7.0375 1.25Z" fill="white" />
										</svg>
										<span>{ translate(setup, 'refine') }</span>
									</a>
								</div>
								<div className={[s.filtering_refineWrapper_item, s.filtering_refineWrapper_item__layout].join(' ')}>
									<a href="#toggle-layout" onClick={this.handleToggleLayout}>
										<span>{ translate(setup, 'switchGrid') }</span>
										{
											layout === 'single' && (
												<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
													<circle cx="2" cy="2" r="1.85" /><circle cx="7" cy="2" r="1.85" /><circle cx="2" cy="7" r="1.85" /><circle cx="7" cy="7" r="1.85" />
												</svg>
											)
										}
										{
											layout === 'default' && (
												<svg width="9" height="10" viewBox="0 0 9 10" fill="none" xmlns="http://www.w3.org/2000/svg">
													<circle cx="1.125" cy="1.64062" r="0.975" /><circle cx="4.5" cy="1.64062" r="0.975" /><circle cx="7.875" cy="1.64062" r="0.975" /><circle cx="1.125" cy="5.01562" r="0.975" /><circle cx="4.5" cy="5.01562" r="0.975" /><circle cx="7.875" cy="5.01562" r="0.975" /><circle cx="1.125" cy="8.39062" r="0.975" /><circle cx="4.5" cy="8.39062" r="0.975" /><circle cx="7.875" cy="8.39062" r="0.975" />
												</svg>
											)
										}
										{
											layout === 'tripple' && (
												<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
													<circle cx="4.5" cy="4.5" r="4.35" />
												</svg>
											)
										}
									</a>
								</div>
								{
									this.state.smallScreenFilter && (
										<div className={s.filtering_refineWrapper_close}>
											<a href="#reset-filter-dropdowns" onClick={this.resetFilterDropdowns}>{ translate(setup, 'close') }</a>
										</div>
									)
								}
							</div>
							<div className={[s.filtering_inner, smallScreenFilterClass].join(' ')} style={setup.breakPoint === 'mobile' ? { height: screenHeight } : {}}>
								<div className={[s.filtering_section, (this.state.filterCategories ? s.filtering_section__open : null)].join(' ')}>
									{/* Categories */}
									<a className={s.filtering_section_title} href="#open-group" data-group="filterCategories" onClick={this.handleToggleGroup}>
										<span>
											{ translate(setup, 'filterCategory') }
											<svg className={s.filtering_section_title_icon} width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4L0.401924 0.25L5.59808 0.25L3 4Z" fill="black" /></svg>
										</span>
									</a>
									<div className={s.filtering_section_inner}>
										{
											Object.keys(filterCategories).sort((a, b) => a.localeCompare(b)).map((category) => {
												if (intersect(filterCategories[category], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_standard];
												if (intersect(filterCategories[category], (lastFilter === 'filterCategories' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_standard__disabled);

												if (filter.filterCategories.indexOf(category) !== -1) {
													linkClasses.push(s.filtering_section_standard__selected);
												}
												return (
													<a key={category} className={linkClasses.join(' ')} onClick={this.handleFilterUpdate} href={`#filterCategories=${category}`}>
														<span>
															{ filterCategoryDescriptions[category] }
															{
																filter.filterCategories.indexOf(category) !== -1 && (
																	<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
																		<path d="M1 4L3.18182 7L7 1" stroke="black" strokeWidth="0.3" strokeLinejoin="bevel" />
																	</svg>
																)
															}
														</span>
													</a>
												);
											})
										}
									</div>
								</div>

								<div className={[s.filtering_section, (this.state.colors ? s.filtering_section__open : null)].join(' ')}>
									{/* Colors */}
									<a className={s.filtering_section_title} href="#open-group" data-group="colors" onClick={this.handleToggleGroup}>
										<span>
											{ translate(setup, 'filterColor') }
											<svg className={s.filtering_section_title_icon} width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4L0.401924 0.25L5.59808 0.25L3 4Z" fill="black" /></svg>
										</span>
									</a>
									<div className={s.filtering_section_inner}>
										{
											Object.keys(colors).sort((a, b) => (parseInt(colorDescriptions[a].order, 10) > parseInt(colorDescriptions[b].order, 10) ? 1 : -1)).map((color) => {
												if (intersect(colors[color], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_color];
												if (intersect(colors[color], (lastFilter === 'colors' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_color__disabled);
												if (filter.colors.indexOf(color) !== -1) linkClasses.push(s.filtering_section_color__selected);

												return (
													<a key={color} className={linkClasses.join(' ')} onClick={this.handleFilterUpdate} href={`#colors=${color}`}>
														<span style={{ backgroundColor: colorDescriptions[color].color }} />
														<span>
															{ colorDescriptions[color].description }
															{
																filter.colors.indexOf(color) !== -1 && (
																	<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
																		<path d="M1 4L3.18182 7L7 1" stroke="black" strokeWidth="0.3" strokeLinejoin="bevel" />
																	</svg>
																)
															}
														</span>
													</a>
												);
											})
										}
									</div>
								</div>

								<div className={[s.filtering_section, (this.state.finishes ? s.filtering_section__open : null)].join(' ')}>
									{/* Finishes */}
									<a className={s.filtering_section_title} href="#open-group" data-group="finishes" onClick={this.handleToggleGroup}>
										<span>
											{ translate(setup, 'filterFinish') }
											<svg className={s.filtering_section_title_icon} width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4L0.401924 0.25L5.59808 0.25L3 4Z" fill="black" /></svg>
										</span>
									</a>
									<div className={s.filtering_section_inner}>
										{
											Object.keys(finishes).sort((a, b) => a.localeCompare(b)).map((finish) => {
												if (intersect(finishes[finish], relevantBaseProducts).length === 0) return null;

												const linkClasses = [s.filtering_section_standard];
												if (intersect(finishes[finish], (lastFilter === 'finishes' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) linkClasses.push(s.filtering_section_standard__disabled);
												if (filter.finishes.indexOf(finish) !== -1) linkClasses.push(s.filtering_section_standard__selected);

												return (
													<a key={finish} className={linkClasses.join(' ')} onClick={this.handleFilterUpdate} href={`#finishes=${finish}`}>
														<span>
															{ finishDescriptions[finish] }
															{
																filter.finishes.indexOf(finish) !== -1 && (
																	<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
																		<path d="M1 4L3.18182 7L7 1" stroke="black" strokeWidth="0.3" strokeLinejoin="bevel" />
																	</svg>
																)
															}
														</span>
													</a>
												);
											})
										}
									</div>
								</div>

								<div className={[s.filtering_section, s.filtering_section__reset, isFiltered ? s.filtering_section__resetActive : null].join(' ')}>
									{/* Reset */}
									<a className={[s.filtering_section_title, s.filtering_section_title_reset].join(' ')} href="#clear-filter" onClick={this.handleClearFilter}>{ translate(setup, 'filterClear') }</a>
									<a className={s.filtering_section_apply} href="#reset-filter-dropdowns" onClick={this.resetFilterDropdowns}>{ translate(setup, 'apply') }</a>
								</div>

								<div className={[s.filtering_section, (this.state.sort ? s.filtering_section__open : null)].join(' ')}>
									{/* Sort */}
									<a className={s.filtering_section_title} href="#open-group" data-group="sort" onClick={this.handleToggleGroup}>
										<span>
											{ translate(setup, 'filterSort') }
											<svg className={s.filtering_section_title_icon} width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4L0.401924 0.25L5.59808 0.25L3 4Z" fill="black" /></svg>
										</span>
									</a>
									<div className={s.filtering_section_inner}>
										<a onClick={this.handleFilterUpdate} className={[s.filtering_section_standard, ...(filter.sort === 'added-descending' ? [s.filtering_section_standard__selected] : [])].join(' ')} href="#sort=added-descending"><span>{ translate(setup, 'sortNewest') }</span></a>
										<a onClick={this.handleFilterUpdate} className={[s.filtering_section_standard, ...(filter.sort === 'price-ascending' ? [s.filtering_section_standard__selected] : [])].join(' ')} href="#sort=price-ascending"><span>{ translate(setup, 'sortCheapest') }</span></a>
										<a onClick={this.handleFilterUpdate} className={[s.filtering_section_standard, ...(filter.sort === 'price-descending' ? [s.filtering_section_standard__selected] : [])].join(' ')} href="#sort=price-descending"><span>{ translate(setup, 'sortMostExpensive') }</span></a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				{
					relevantProducts.length > 0 ? (
						<div className={s.products}>
							<Pagination ListComponent={ListComponent} list={relevantProducts} cache={products} currentPath={prefix(setup, currentPath)} currentHash="" showAll={location.query && location.query.showAll !== undefined} contentSections={contentSections} contentOffset={contentOffset} layout={layout} />
						</div>
					) : (
						<div className={s.filter_empty}>
							<h1 className={s.filter_empty_title}>{ relevantBaseProducts.length > 0 ? translate(setup, 'filterNoResults') : translate(setup, 'filterNoResultsNoFilter') }</h1>
							{
								filter.filteredBy.map((filterObj) => (
									Object.keys(filterObj).map((filterKey) => {
										if (Array.isArray(filterObj[filterKey])) {
											return filterObj[filterKey].map((filterItem) => {
												let title = filterDescriptions[filterKey][filterItem];
												if (filterKey === 'filterCategories') title = filterCategoryDescriptions[filterItem];
												if (filterKey === 'colors') title = colorDescriptions[filterItem];
												if (filterKey === 'finishes') title = finishDescriptions[filterItem];
												return <a className={s.filter_empty_link} href={`#${filterKey}=${filterItem}`} onClick={this.handleFilterUpdate}>{ title }</a>;
											});
										}
										return <a className={s.filter_empty_link} href={`#${filterKey}=1`} onClick={this.handleFilterUpdate}>{ translate(setup, `filter${filterKey.charAt(0).toUpperCase()}${filterKey.slice(1)}`) }</a>;
									})
								))
							}
						</div>
					)
				}
			</div>
		);
	}
}

const mapState = ({ filter, location }, { ListComponent, data, filtering, setup, currentPath, productUri = false }) => ({
	filter,
	location,
	ListComponent,
	data,
	filtering,
	setup,
	currentPath,
	productUri,
});

export default hot(module)(connect(mapState, { updateFilter, resetFilter, clearFilter, updatePagination })(Filter));
